import type { Quote } from '@client/model';
import type { TraditionalLTCQuoteWithROI } from '@/features/shop-insurance/types';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  roiValues?: any;
}

/**
 * Groups quotes by product name and selects the highest ROI quote for each product
 * @param quotes Array of quotes (life insurance or traditional LTC)
 * @returns Array of unique product quotes sorted by ROI descending
 */
export function groupQuotesByProduct<T extends QuoteWithROI | TraditionalLTCQuoteWithROI>(
  quotes: T[],
): T[] {
  if (!quotes || quotes.length === 0) {
    return [];
  }

  // Group quotes by productName
  const groupedByProduct = quotes.reduce(
    (acc, quote) => {
      const productName = quote.productName;
      if (!productName) return acc;

      if (!acc[productName]) {
        acc[productName] = [];
      }
      acc[productName].push(quote);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  // Select the highest ROI quote for each product
  const uniqueProductQuotes = Object.values(groupedByProduct).map(productQuotes => {
    // Sort by ROI descending and take the first (highest ROI)
    return productQuotes.sort((a, b) => {
      const roiA = a.roi ?? 0;
      const roiB = b.roi ?? 0;
      return roiB - roiA;
    })[0];
  });

  // Sort all unique product quotes by ROI descending
  return uniqueProductQuotes.sort((a, b) => {
    const roiA = a.roi ?? 0;
    const roiB = b.roi ?? 0;
    return roiB - roiA;
  });
}
